import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Grid,
  Inline,
  KpiCard,
  PageHeader,
  Panel,
  SelectField,
  Spinner,
  Stack,
  StatusBadge
} from "@leylion/design-system";
import {
  exportEffectiveSettings,
  getBranches,
  getDevices,
  getSettingsCatalog,
  getSettingsHistory,
  getSettingsOverview
} from "../api/client";
import type { ContextOption } from "../api/types";
import type {
  Branch,
  Device,
  SettingsCatalog,
  SettingsHistoryItem,
  SettingsOverview,
  SettingsScopeType,
  SettingsSectionDefinition
} from "../api/settings-types";
import { BranchesPanel } from "./BranchesPanel";
import { OrganizationProfilePanel } from "./OrganizationProfilePanel";
import { SettingsSectionEditor } from "./SettingsSectionEditor";
import { formatDate } from "./settings-utils";
import "./settings.css";

type Language = "ar" | "en";
type ViewKey = "overview" | "organization" | "branches" | "history" | string;

export function SettingsCenter({
  language,
  context
}: {
  language: Language;
  context: ContextOption;
}) {
  const [catalog, setCatalog] = useState<SettingsCatalog | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [overview, setOverview] = useState<SettingsOverview | null>(null);
  const [history, setHistory] = useState<SettingsHistoryItem[]>([]);
  const [scopeType, setScopeType] = useState<SettingsScopeType>("Organization");
  const [scopeId, setScopeId] = useState(context.tenantId);
  const [view, setView] = useState<ViewKey>("overview");
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const ar = language === "ar";
  const currentBranch = branches.find((branch) => branch.id === context.branchId);
  const scopedBranch = scopeType === "Branch"
    ? branches.find((branch) => branch.id === scopeId)
    : currentBranch;
  const settingsCurrency = scopedBranch?.currencyOverride ?? currentBranch?.currencyOverride ?? "AED";

  const loadReferenceData = async () => {
    setBusy(true);
    setError("");
    try {
      const [catalogResult, branchResult, deviceResult] = await Promise.all([
        getSettingsCatalog(),
        getBranches(),
        getDevices()
      ]);
      setCatalog(catalogResult);
      setBranches(branchResult);
      setDevices(deviceResult);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load settings center.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void loadReferenceData();
  }, []);

  useEffect(() => {
    if (!catalog || !scopeId) return;
    getSettingsOverview(scopeType, scopeId)
      .then(setOverview)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "Unable to load readiness.")
      );
  }, [catalog, scopeType, scopeId, refreshKey]);

  useEffect(() => {
    if (view !== "history") return;
    getSettingsHistory(scopeType, scopeId)
      .then(setHistory)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "Unable to load history.")
      );
  }, [view, scopeType, scopeId, refreshKey]);

  const selectedSection = useMemo(
    () => catalog?.sections.find((section) => section.key === view) ?? null,
    [catalog, view]
  );

  const availableSections = useMemo(
    () =>
      catalog?.sections.filter((section) =>
        section.allowedScopes.includes(scopeType)
      ) ?? [],
    [catalog, scopeType]
  );

  const changeScopeType = (next: SettingsScopeType) => {
    setScopeType(next);
    if (next === "Organization") {
      setScopeId(context.tenantId);
    } else if (next === "Branch") {
      setScopeId(context.branchId || branches[0]?.id || "");
    } else {
      setScopeId(
        devices.find((device) => device.branchId === context.branchId)?.id ??
          devices[0]?.id ??
          ""
      );
    }

    if (
      selectedSection &&
      !selectedSection.allowedScopes.includes(next)
    ) {
      setView("overview");
    }
  };

  const refresh = () => setRefreshKey((value) => value + 1);

  if (busy && !catalog) {
    return (
      <Card>
        <Inline>
          <Spinner label={ar ? "جارٍ التحميل" : "Loading"} />
          <span>{ar ? "جارٍ تجهيز مركز الإعدادات…" : "Preparing settings center…"}</span>
        </Inline>
      </Card>
    );
  }

  if (!catalog) {
    return (
      <Alert tone="danger" title={ar ? "تعذر فتح الإعدادات" : "Settings unavailable"}>
        {error}
      </Alert>
    );
  }

  return (
    <Stack>
      <PageHeader
        eyebrow={ar ? "إدارة النظام" : "System Administration"}
        title={ar ? "مركز إعداد Leylion" : "Leylion Settings Center"}
        description={
          ar
            ? "يعرض هذا المركز الإعدادات المرتبطة بسلوك تشغيلي فعلي فقط؛ تبقى الإعدادات المستقبلية مخفية حتى يتم ربطها بوحدة منتجة."
            : "This center exposes only settings with an active runtime consumer; future settings stay hidden until they are wired to product behavior."
        }
        actions={
          <Inline>
            <Button
              type="button"
              variant="secondary"
              icon="download"
              onClick={() =>
                void exportEffectiveSettings(
                  scopeType === "Branch" ? scopeId : context.branchId,
                  scopeType === "Device" ? scopeId : undefined
                )
              }
            >
              {ar ? "تصدير الإعدادات الفعالة" : "Export effective settings"}
            </Button>
          </Inline>
        }
      />

      {error ? (
        <Alert tone="danger" title={ar ? "حدث خطأ" : "An error occurred"} onDismiss={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Panel className="settings-scope-bar">
        <div className="settings-scope-grid">
          <SelectField
            data-testid="settings-scope-type"
            label={ar ? "مستوى الإعداد" : "Settings scope"}
            value={scopeType}
            onChange={(event) => changeScopeType(event.target.value as SettingsScopeType)}
          >
            <option value="Organization">{ar ? "المؤسسة" : "Organization"}</option>
            <option value="Branch">{ar ? "الفرع" : "Branch"}</option>
            <option value="Device">{ar ? "الجهاز" : "Device"}</option>
          </SelectField>

          {scopeType === "Branch" ? (
            <SelectField
              data-testid="settings-scope-branch"
              label={ar ? "الفرع" : "Branch"}
              value={scopeId}
              onChange={(event) => setScopeId(event.target.value)}
            >
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name} · {branch.code}
                </option>
              ))}
            </SelectField>
          ) : null}

          {scopeType === "Device" ? (
            <SelectField
              data-testid="settings-scope-device"
              label={ar ? "الجهاز" : "Device"}
              value={scopeId}
              onChange={(event) => setScopeId(event.target.value)}
            >
              {devices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.name} · {device.code}
                </option>
              ))}
            </SelectField>
          ) : null}

          <div className="settings-scope-summary">
            <strong>
              {scopeType === "Organization"
                ? ar ? "إعدادات المؤسسة" : "Organization settings"
                : scopeType === "Branch"
                  ? ar ? "تخصيص الفرع" : "Branch overrides"
                  : ar ? "تخصيص الجهاز" : "Device overrides"}
            </strong>
            <small>
              {scopeType === "Organization"
                ? ar
                  ? "تطبق على جميع الفروع ما لم يوجد تخصيص."
                  : "Applies to every branch unless an override exists."
                : ar
                  ? "يمكن العودة إلى القيمة الموروثة في أي حقل."
                  : "Every field can return to its inherited value."}
            </small>
          </div>
        </div>
      </Panel>

      <div className="settings-center-layout">
        <aside className="settings-navigation">
          <button
            type="button"
            className={view === "overview" ? "is-active" : ""}
            onClick={() => setView("overview")}
          >
            <strong>{ar ? "نظرة عامة" : "Overview"}</strong>
            <small>{ar ? "الصحة والتقدم" : "Health & readiness"}</small>
          </button>
          <button
            type="button"
            className={view === "organization" ? "is-active" : ""}
            onClick={() => setView("organization")}
          >
            <strong>{ar ? "ملف المؤسسة" : "Organization profile"}</strong>
            <small>{ar ? "البيانات القانونية" : "Legal source of truth"}</small>
          </button>
          <button
            type="button"
            className={view === "branches" ? "is-active" : ""}
            onClick={() => setView("branches")}
          >
            <strong>{ar ? "إدارة الفروع" : "Branch management"}</strong>
            <small>{ar ? "المواقع وساعات العمل" : "Locations & hours"}</small>
          </button>

          {catalog.categories.map((category) => {
            const categorySections = availableSections.filter(
              (section) => section.category === category.key
            );
            if (categorySections.length === 0) return null;

            return (
              <div className="settings-navigation__group" key={category.key}>
                <h3>{ar ? category.labelAr : category.labelEn}</h3>
                {categorySections.map((section) => (
                  <button
                    type="button"
                    key={section.key}
                    className={view === section.key ? "is-active" : ""}
                    onClick={() => setView(section.key)}
                  >
                    <span>
                      <strong>{ar ? section.titleAr : section.titleEn}</strong>
                      <small>
                        {section.level === "advanced"
                          ? ar ? "متقدم" : "Advanced"
                          : ar ? "أساسي" : "Basic"}
                      </small>
                    </span>
                    {overview?.sections.find(
                      (item) => item.sectionKey === section.key
                    )?.hasUnpublishedChanges ? (
                      <span className="settings-draft-dot" title={ar ? "مسودة" : "Draft"} />
                    ) : null}
                  </button>
                ))}
              </div>
            );
          })}

          <button
            type="button"
            className={view === "history" ? "is-active" : ""}
            onClick={() => setView("history")}
          >
            <strong>{ar ? "سجل التغييرات" : "Change history"}</strong>
            <small>{ar ? "المسودات والنشر" : "Drafts & publications"}</small>
          </button>
        </aside>

        <main className="settings-content">
          {view === "overview" ? (
            <SettingsOverviewView
              overview={overview}
              language={language}
              onOpenSection={setView}
            />
          ) : view === "organization" ? (
            <OrganizationProfilePanel language={language} onChanged={refresh} />
          ) : view === "branches" ? (
            <BranchesPanel language={language} onChanged={() => {
              refresh();
              void loadReferenceData();
            }} />
          ) : view === "history" ? (
            <SettingsHistoryView history={history} catalog={catalog} language={language} />
          ) : selectedSection ? (
            <SettingsSectionEditor
              section={selectedSection}
              scopeType={scopeType}
              scopeId={scopeId}
              language={language}
              currency={settingsCurrency}
              onStateChanged={refresh}
            />
          ) : (
            <EmptyState
              icon="settings"
              title={ar ? "القسم غير متاح" : "Section unavailable"}
            />
          )}
        </main>
      </div>
    </Stack>
  );
}

function SettingsOverviewView({
  overview,
  language,
  onOpenSection
}: {
  overview: SettingsOverview | null;
  language: Language;
  onOpenSection: (sectionKey: string) => void;
}) {
  const ar = language === "ar";

  if (!overview) {
    return (
      <Card>
        <Inline>
          <Spinner label={ar ? "جارٍ التحميل" : "Loading"} />
          <span>{ar ? "جارٍ حساب الجاهزية…" : "Calculating readiness…"}</span>
        </Inline>
      </Card>
    );
  }

  return (
    <Stack>
      <Grid columns={4}>
        <KpiCard
          label={ar ? "جاهزية الإعدادات" : "Settings readiness"}
          value={`${overview.overallCompletionPercent}%`}
          trend={overview.overallCompletionPercent === 100 ? (ar ? "مكتمل" : "Complete") : (ar ? "قيد الإكمال" : "In progress")}
          trendTone={overview.overallCompletionPercent === 100 ? "success" : "warning"}
        />
        <KpiCard
          label={ar ? "الأقسام المنشورة" : "Published sections"}
          value={`${overview.publishedSections}/${overview.totalSections}`}
          trendTone="info"
        />
        <KpiCard
          label={ar ? "المسودات" : "Drafts"}
          value={overview.draftSections}
          trend={overview.draftSections ? (ar ? "تحتاج نشرًا" : "Need publishing") : (ar ? "لا توجد" : "None")}
          trendTone={overview.draftSections ? "warning" : "success"}
        />
        <KpiCard
          label={ar ? "النطاق" : "Scope"}
          value={overview.scopeType}
          hint={overview.scopeId.slice(0, 8)}
        />
      </Grid>

      {overview.readinessWarnings.length > 0 ? (
        <Alert tone="warning" title={ar ? "نقاط تحتاج إكمالًا" : "Readiness gaps"}>
          {overview.readinessWarnings.join(" · ")}
        </Alert>
      ) : (
        <Alert tone="success" title={ar ? "الإعدادات المطلوبة مكتملة" : "Required settings are complete"}>
          {ar
            ? "جميع الحقول الإلزامية في هذا النطاق لها قيم فعالة."
            : "All required fields in this scope have effective values."}
        </Alert>
      )}

      <Panel
        title={ar ? "هيكل الإعدادات الكامل" : "Complete settings structure"}
        description={
          ar
            ? "افتح أي قسم لتعديل القيم ومراجعة التأثير قبل النشر."
            : "Open any section to edit values and review impact before publishing."
        }
      >
        <div className="settings-overview-grid">
          {overview.sections.map((item) => (
            <button
              type="button"
              className="settings-overview-card"
              key={item.sectionKey}
              onClick={() => onOpenSection(item.sectionKey)}
            >
              <div>
                <strong>{ar ? item.titleAr : item.titleEn}</strong>
                <small>{item.impacts.slice(0, 3).join(" · ")}</small>
              </div>
              <div>
                <StatusBadge tone={item.hasUnpublishedChanges ? "warning" : "success"}>
                  {item.hasUnpublishedChanges
                    ? ar ? "مسودة" : "Draft"
                    : ar ? "منشور" : "Published"}
                </StatusBadge>
                <span className="settings-progress">
                  <i style={{ width: `${item.completionPercent}%` }} />
                </span>
                <small>{item.completionPercent}%</small>
              </div>
            </button>
          ))}
        </div>
      </Panel>
    </Stack>
  );
}

function SettingsHistoryView({
  history,
  catalog,
  language
}: {
  history: SettingsHistoryItem[];
  catalog: SettingsCatalog;
  language: Language;
}) {
  const ar = language === "ar";

  return (
    <Panel
      title={ar ? "سجل تغييرات الإعدادات" : "Settings change history"}
      description={
        ar
          ? "سجل الإصدارات والمسودات والنشر ضمن النطاق الحالي."
          : "Revision, draft and publication history for the current scope."
      }
    >
      {history.length === 0 ? (
        <EmptyState
          icon="reports"
          title={ar ? "لا توجد تغييرات" : "No changes yet"}
        />
      ) : (
        <div className="settings-history-table">
          <table>
            <thead>
              <tr>
                <th>{ar ? "القسم" : "Section"}</th>
                <th>{ar ? "الحالة" : "Status"}</th>
                <th>{ar ? "المراجعة" : "Revision"}</th>
                <th>{ar ? "آخر تحديث" : "Updated"}</th>
                <th>{ar ? "النشر" : "Published"}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => {
                const definition = catalog.sections.find(
                  (section) => section.key === item.sectionKey
                );
                return (
                  <tr key={item.id}>
                    <td>
                      <strong>
                        {definition
                          ? ar ? definition.titleAr : definition.titleEn
                          : item.sectionKey}
                      </strong>
                    </td>
                    <td>
                      <StatusBadge
                        tone={
                          item.status === "Published"
                            ? "success"
                            : item.status === "Draft"
                              ? "warning"
                              : "neutral"
                        }
                      >
                        {item.status}
                      </StatusBadge>
                    </td>
                    <td>#{item.revisionNumber} · v{item.version}</td>
                    <td>{formatDate(item.updatedAt, language)}</td>
                    <td>{formatDate(item.publishedAt, language)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
